import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog'
import { Button } from '../ui/button'
import { Label } from '../ui/label'
import { Clock, RefreshCw, Loader2, CalendarClock } from 'lucide-react'
import { Formik, Form, ErrorMessage } from 'formik'
import * as Yup from 'yup'
import { useUpdateSyncScheduleMutation } from '@/redux/service/configuration'
import toast from 'react-hot-toast'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select'

const ScheduleSchema = Yup.object().shape({
  sync_frequency: Yup.number().required('Frequency is required').min(1),
  scheduled_hour: Yup.number()
    .required('Scheduled hour is required')
    .min(0)
    .max(23),
})

interface ScheduleFormValues {
  sync_frequency: number
  scheduled_hour: number
}

interface ScheduleUpdateModalProps {
  isOpen: boolean
  onClose: () => void
  initialData?: {
    sync_frequency: number | undefined
    scheduled_hour: number | undefined
  }
}

const ScheduleUpdateModal = ({
  isOpen,
  onClose,
  initialData,
}: ScheduleUpdateModalProps) => {
  const [updateSchedule, { isLoading }] = useUpdateSyncScheduleMutation()

  const handleSubmit = async (values: ScheduleFormValues) => {
    try {
      const res = await updateSchedule(values).unwrap()
      toast.success(res.message || 'Schedule updated successfully!')
      onClose()
    } catch (err) {
      const message =
        (err as { data?: { message?: string } })?.data?.message ||
        'Failed to update schedule'

      toast.error(message)
    }
  }

  const hours = Array.from({ length: 24 }, (_, i) => ({
    value: i,
    label:
      i === 0
        ? '12 AM'
        : i < 12
          ? `${i} AM`
          : i === 12
            ? '12 PM'
            : `${i - 12} PM`,
  }))

  const frequencies = [
    { value: 6, label: '6 Hours' },
    { value: 12, label: '12 Hours' },
    { value: 24, label: '24 Hours' },
  ]

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className='max-w-[400px] p-0 overflow-hidden bg-background border-none shadow-2xl rounded-[24px]'>
        <DialogHeader className='p-8 pb-6 border-b border-border/30 bg-card/80 backdrop-blur-xl'>
          <DialogTitle className='text-2xl font-bold tracking-tight text-foreground flex items-center gap-3'>
            <div className='p-2 bg-primary/10 text-primary rounded-xl'>
              <CalendarClock className='w-6 h-6' />
            </div>
            Update Schedule
          </DialogTitle>
          <p className='text-[14px] text-muted-foreground mt-1.5 font-medium'>
            Configure how often ClickUp data should be synchronized.
          </p>
        </DialogHeader>

        <Formik
          initialValues={{
            sync_frequency: initialData?.sync_frequency || 6,
            scheduled_hour: initialData?.scheduled_hour || 1,
          }}
          validationSchema={ScheduleSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ setFieldValue, values }) => (
            <Form className='p-8 space-y-6 bg-card'>
              <div className='space-y-5'>
                <div className='space-y-2.5'>
                  <Label className='text-[13px] font-bold text-foreground uppercase tracking-wider ml-1 flex items-center gap-2'>
                    <RefreshCw className='w-3.5 h-3.5 text-primary' />
                    Sync Frequency
                  </Label>
                  <Select
                    value={values.sync_frequency.toString()}
                    onValueChange={(val: string) =>
                      setFieldValue('sync_frequency', parseInt(val))
                    }
                  >
                    <SelectTrigger className='w-full h-12 rounded-2xl border-border/60 bg-muted hover:bg-background hover:border-primary transition-all font-medium'>
                      <SelectValue placeholder='Select frequency' />
                    </SelectTrigger>
                    <SelectContent className='rounded-xl border-border/40 shadow-xl'>
                      {frequencies.map(f => (
                        <SelectItem
                          key={f.value}
                          value={f.value.toString()}
                          className='rounded-lg cursor-pointer'
                        >
                          {f.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <ErrorMessage
                    name='sync_frequency'
                    component='div'
                    className='text-xs text-destructive font-medium ml-1'
                  />
                </div>

                <div className='space-y-2.5'>
                  <Label className='text-[13px] font-bold text-foreground uppercase tracking-wider ml-1 flex items-center gap-2'>
                    <Clock className='w-3.5 h-3.5 text-primary' />
                    Scheduled Hour
                  </Label>
                  <Select
                    value={values.scheduled_hour.toString()}
                    onValueChange={(val: string) =>
                      setFieldValue('scheduled_hour', parseInt(val))
                    }
                  >
                    <SelectTrigger className='w-full h-12 rounded-2xl border-border/60 bg-muted hover:bg-background hover:border-primary transition-all font-medium'>
                      <SelectValue placeholder='Select hour' />
                    </SelectTrigger>
                    <SelectContent className='rounded-xl border-border/40 shadow-xl max-h-[300px]'>
                      {hours.map(h => (
                        <SelectItem
                          key={h.value}
                          value={h.value.toString()}
                          className='rounded-lg cursor-pointer'
                        >
                          {h.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <ErrorMessage
                    name='scheduled_hour'
                    component='div'
                    className='text-xs text-destructive font-medium ml-1'
                  />
                </div>
              </div>

              <div className='flex items-center justify-between pt-6 border-t border-muted'>
                <Button
                  type='button'
                  variant='ghost'
                  onClick={onClose}
                  className='rounded-xl text-muted-foreground hover:text-foreground font-semibold transition-colors'
                >
                  Cancel
                </Button>
                <Button
                  type='submit'
                  disabled={isLoading}
                  className='rounded-2xl bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-12 font-bold transition-all duration-300 shadow-lg hover:shadow-primary/30 flex items-center gap-2'
                >
                  {isLoading ? (
                    <>
                      <Loader2 className='w-5 h-5 animate-spin' />
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <span>Save Changes</span>
                    </>
                  )}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}

export default ScheduleUpdateModal
